import { Box } from "@mui/material";
import React from "react";
import useTheme from "../../../../../../../common/hooks/useTheme";
import { Theme } from "../../../../../../../providers/ThemeProvider";
import { Diet_Types } from "../../model/dietTypes";
import { FoodType, RecipeType } from "../../model/nutritionPlanTypes";
import PlanTag from "../shared/PlanTag";

type Props =
  | { kind: "food"; item: FoodType }
  | { kind: "recipe"; item: RecipeType };

type Allergen = { name: string; allergies: number };

/** A food's own allergens, or the union of a recipe's ingredient allergens. */
const getAllergens = (props: Props): Allergen[] => {
  const lists =
    props.kind === "food"
      ? [props.item.allergens ?? []]
      : (props.item.items ?? []).map((item) => item.food?.allergens ?? []);
  const seen = new Set<number>();
  const result: Allergen[] = [];
  lists.flat().forEach((allergen) => {
    if (!seen.has(allergen.allergies)) {
      seen.add(allergen.allergies);
      result.push(allergen);
    }
  });
  return result;
};

const getDietNames = (item: FoodType | RecipeType): string[] =>
  (item.diet_types ?? [])
    .map((dietType) => Diet_Types[dietType.diet_type]?.name)
    .filter((name): name is string => !!name);

/** Mobile `RecipeStorageInfo`: keyed on `consume_within_days`. */
const getStorageText = (recipe: RecipeType): string => {
  const days = recipe.consume_within_days;
  if (days && days > 0) {
    return `Keep refrigerated and use within ${days} day${
      days > 1 ? "s" : ""
    }.`;
  }
  return "Store covered in refrigerator for best freshness.";
};

/** Mobile `RecipeFunFact`: foods carry their own fact, recipes get a generated one. */
const getFunFact = (props: Props): string | null => {
  if (props.kind === "food") {
    const fact = props.item.fun_fact?.trim();
    return fact ? fact : null;
  }
  if (props.item.key_ingredient) {
    return `Did you know? ${props.item.key_ingredient} is a key ingredient that adds both nutritional value and flavor complexity to this delicious recipe.`;
  }
  return "This recipe combines traditional cooking methods with modern nutritional understanding for optimal taste and health benefits.";
};

/** Mobile `RecipeHealthBenefits` shows one fixed line whenever benefits exist. */
const HEALTH_BENEFITS_TEXT =
  "Gut microbiome support from fiber-rich ingredients and balanced macronutrients for sustained energy.";

/**
 * The "Additional information" block of the details panel: allergens,
 * storage (recipes), health benefits, fun fact and diet classifications.
 */
const AdditionalInformation = (props: Props) => {
  const theme: Theme = useTheme();
  const { item } = props;

  const allergens = getAllergens(props);
  const dietNames = getDietNames(item);
  const funFact = getFunFact(props);
  const hasBenefits = (item.health_benefits?.length ?? 0) > 0;

  const Section = ({
    title,
    children,
  }: {
    title: string;
    children: React.ReactNode;
  }) => (
    <Box sx={styles.section}>
      <Box sx={styles.sectionTitle(theme)}>{title}</Box>
      {children}
    </Box>
  );

  return (
    <Box sx={styles.container}>
      <Box sx={styles.title(theme)}>Additional information</Box>

      <Section title="Allergens">
        {allergens.length === 0 ? (
          <Box sx={styles.text(theme)}>No allergen information available.</Box>
        ) : (
          <Box sx={styles.tags}>
            {allergens.map((allergen) => (
              <PlanTag
                key={allergen.allergies}
                kind="content"
                label={allergen.name}
              />
            ))}
          </Box>
        )}
      </Section>

      {props.kind === "recipe" && (
        <Section title="Storage">
          <Box sx={styles.text(theme)}>{getStorageText(props.item)}</Box>
        </Section>
      )}

      {hasBenefits && (
        <Section title="Health benefits">
          <Box sx={styles.text(theme)}>{HEALTH_BENEFITS_TEXT}</Box>
        </Section>
      )}

      {funFact && (
        <Section title="Fun fact">
          <Box sx={styles.text(theme)}>{funFact}</Box>
        </Section>
      )}

      {dietNames.length > 0 && (
        <Section title="Diet classifications">
          <Box sx={styles.tags}>
            {dietNames.map((name) => (
              <PlanTag key={name} kind="content" label={name} />
            ))}
          </Box>
        </Section>
      )}
    </Box>
  );
};

export default AdditionalInformation;

const styles = {
  container: {
    display: "flex",
    flexDirection: "column" as "column",
    gap: "16px",
  },
  title: (theme: Theme) => ({
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
    lineHeight: "20px",
  }),
  section: {
    display: "flex",
    flexDirection: "column" as "column",
    gap: "8px",
  },
  sectionTitle: (theme: Theme) => ({
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    lineHeight: "16px",
  }),
  text: (theme: Theme) => ({
    color: theme.colors_ds.NEUTRAL_TEXT_STRONG,
    fontFamily: "var(--font-Regular)",
    fontSize: "14px",
    lineHeight: "22px",
  }),
  tags: {
    display: "flex",
    flexWrap: "wrap" as "wrap",
    gap: "8px",
  },
};
